/**
 * AIMDv2 – StepBar
 *
 * Horizontal progress indicator at the top of the wizard.
 * Each step is rendered as a coloured bar + label. Steps can be in one of
 * three visual states: "completed" (solid highlight), "current" (solid
 * highlight), or "default" (dim border).
 *
 * Step labels carry no sparkle: the sparkle marks a control that makes
 * Elliott do something, not a step (StepMeta.showIcon is no longer drawn).
 *
 * Clicking a completed or current step navigates to it (if allowed — never
 * backwards once the wizard has reached Structure; see canNavigateToStep).
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { StepMeta, StepState } from '../types';
import { STEP_META, canNavigateToStep } from '../constants';

interface StepBarProps {
  currentStep: number;
  highestCompletedStep: number;
  onStepClick: (index: number) => void;
}

/** Derive the visual state for a given step index. */
function getStepState(
  index: number,
  currentStep: number,
  highestCompletedStep: number
): StepState {
  if (index === currentStep) return 'current';
  if (index <= highestCompletedStep) return 'completed';
  return 'default';
}

/** Single step indicator. */
const StepIndicator: React.FC<{
  meta: StepMeta;
  state: StepState;
  clickable: boolean;
  onClick: () => void;
}> = ({ meta, state, clickable, onClick }) => {
  const isActive = state === 'current' || state === 'completed';

  return (
    <Box
      onClick={clickable ? onClick : undefined}
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        cursor: isActive && clickable ? 'pointer' : 'default',
        opacity: isActive ? 1 : 0.7
      }}
    >
      {/* Progress bar segment */}
      <Box
        sx={{
          width: '100%',
          height: '5px',
          borderRadius: '50px',
          ...(isActive
            ? { background: 'var(--Stroke-highlight, #F79420)' }
            : {
                border: '1px solid var(--Stroke-Step-step-dim, #565656)',
                background: 'transparent'
              })
        }}
      />

      {/* Label */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '3px'
        }}
      >
        <Typography
          sx={{
            fontSize: '10px',
            lineHeight: '10px',
            fontFamily: 'Inter',
            fontWeight: state === 'current' ? 700 : 400,
            color: isActive
              ? 'var(--Stroke-highlight, #F79420)'
              : 'var(--Stroke-Step-step-dim, #565656)',
            textAlign: 'center'
          }}
        >
          {meta.label}
        </Typography>
      </Box>
    </Box>
  );
};

const StepBar: React.FC<StepBarProps> = ({
  currentStep,
  highestCompletedStep,
  onStepClick
}) => {
  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 750,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: '14px',
        mx: 'auto'
      }}
    >
      {STEP_META.map((meta, index) => (
        <StepIndicator
          key={meta.id}
          meta={meta}
          state={getStepState(index, currentStep, highestCompletedStep)}
          clickable={canNavigateToStep(currentStep, index)}
          onClick={() => onStepClick(index)}
        />
      ))}
    </Box>
  );
};

export default StepBar;
